import InputGroupContent from '@/components/elements/input'
import Submit from '@/components/elements/submit'
import {
	AccordionItem,
	AccordionItemContent,
	AccordionItemTrigger,
	AccordionRoot,
} from '@/components/ui/accordion'
import Icon from '@/components/shared/icons'
import {
	Badge,
	Box,
	Button,
	Flex,
	Grid,
	Heading,
	Image,
	Input,
	Separator,
	Stack,
	Text,
	Textarea,
	VStack,
} from '@chakra-ui/react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/store/store'
import { API_URL } from '@/services/api/keys'
import { useEffect, useState } from 'react'
import formatCurrency from '@/utils/price-convert'
import entityConvert from '@/utils/entity-convert'
import { Entities } from '@/entities/api/entities'
import DateConvert from '@/utils/date-convert'
import { PaymethodsConvert } from '@/utils/paymethods-convert'
import { WompiStatusConvert } from '@/utils/wompi-status-convert'
import { OrderStatusColor, OrderStatusConvert } from '@/utils/order-status'
import { getJoinUser } from '@/utils/get-join-user'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { shippingInfoSchema } from '@/entities/schemas/shipping'
import { InferType } from 'yup'
import { useUploader } from '@/hooks/useUploader'
import useManualProgress from '@/hooks/useManualProgress'
import { useImageUploadToast } from '@/hooks/useImageUploadToast'
import Toast from '@/components/elements/toast'
import { shippingTypeMap } from '@/utils/maps/shipping-type-map'
import Select from '@/components/elements/select'
import SpinnerLoading from '@/components/elements/spinner'
import ImageItem from './parts/stock/image-item'
import {
	OrderStatus,
	ShippingInfo,
	ShippingType,
} from '@/entities/api/orders/orders'
import { getDate } from '@/utils/get-date'
import {
	updateOrderDenialById,
	updateOrderRefundedById,
	updateOrderShippingInfoById,
	updateOrderShippingInfoWithoutImageById,
	updateOrderStatusById,
} from '@/services/api/orders/orders'
import {
	ordersRefrestUpdate,
	sessionRefrestUpdate,
} from '@/store/features/state'
import {
	updateDenialOrderFn,
	updateShippingInfoOrderFn,
	updateStatusOrderFn,
} from '@/store/features/orders'
import { ToastConfirm } from '@/components/elements/toast-confirm'
import { InputGroup } from '@/components/ui/input-group'
import { Field } from '@/components/ui/field'
import { TypesCoupon } from '@/entities/api/coupons/coupons'
import nameAttributeConvert from '@/utils/name-attributes-convert'
import { useFormErrorToast } from '@/hooks/useFormErrorToast'

type UserFormValues = InferType<typeof shippingInfoSchema>

const OpenOrder = ({ close }: { close: () => void }) => {
	const selectOrder = useSelector(
		(state: RootState) => state.orders.selectOrder
	)
	const order = selectOrder
	if (!order) {
		return null
	}
	const {
		register,
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<UserFormValues>({
		resolver: yupResolver(shippingInfoSchema),
	})
	useFormErrorToast(errors)
	const token = useSelector((state: RootState) => state.auth.token)
	const [selectPrice, setSelectPrice] = useState<number>(
		order.shipping_info?.price ?? 0
	)
	const [denialReason, setDenialReason] = useState<string>('')
	const [refundedReason, setRefundedReason] = useState<string>('')
	const dispatch = useDispatch<AppDispatch>()
	const {
		inputRef,
		files,
		isProcessing,
		handleFileChange,
		clearFiles,
		removeFile,
	} = useUploader()
	const [isUploadImage, setIsUploadImage] = useState<boolean>(false)
	const { progress, isLoading, startProgress, completeProgress, failProgress } =
		useManualProgress()
	const { handleProgress } = useImageUploadToast()

	const deleteCurrentImage = () => {
		dispatch(
			updateShippingInfoOrderFn({
				_id: order._id,
				shipping_info: {
					...order.shipping_info,
					evidenced: {
						_id: '',
						url: '',
					},
				},
			})
		)
	}

	useEffect(() => {
		if (files.length > 0) {
			setIsUploadImage(true)
			Toast({
				type: 'success',
				duration: 1000,
				message: 'La imagen se cargo correctamente',
			})
		}
		if (files.length >= 1) {
			Toast({
				type: 'info',
				duration: 1000,
				message: 'Solo puedes agregar 1 images de evidencia',
			})
		}
	}, [files])

	useEffect(() => {
		if (progress !== undefined && isLoading !== undefined) {
			handleProgress(progress, isLoading)
		}
	}, [progress, isLoading])

	const onSubmit = async (data: UserFormValues) => {
		try {
			if (selectPrice && selectPrice <= 0) {
				Toast({
					type: 'error',
					message: 'Debes agregar un precio de envío valido',
				})
				await failProgress()
				return
			}

			startProgress()

			let response: any

			const shipping_info: Partial<ShippingInfo> = {
				company: data.company,
				company_address: data.company_address,
				code: data.code,
				date: getDate(),
				estimated_date: data.estimated_date,
				type: data.type as ShippingType,
				price: selectPrice || 0,
			}

			if (files.length < 1) {
				response = await updateOrderShippingInfoWithoutImageById(
					token ?? '',
					order._id,
					{ ...shipping_info, evidenced: order.shipping_info?.evidenced }
				)
			} else {
				const images: File[] = files.map((file) => file.file)

				response = await updateOrderShippingInfoById(
					token ?? '',
					order._id,
					shipping_info,
					images
				)
			}

			if (response?.status === 413) {
				Toast({
					type: 'error',
					message: 'El archivo es muy grande para ser procesado por el api',
				})
				await failProgress()
				return
			}

			if (
				response?.status === 409 ||
				response?.status === 404 ||
				response?.status === 400
			) {
				Toast({
					type: 'error',
					message: 'Hubo un error al actualizar la informacion de entrega',
				})
				await failProgress()
				return
			}

			if (response?.status === 401) {
				dispatch(sessionRefrestUpdate())
				await failProgress()
				return
			}

			if (order?.status === OrderStatus.processing) {
				await updateOrderStatus(OrderStatus.on_the_way)
			}

			dispatch(
				updateShippingInfoOrderFn({
					_id: order._id,
					shipping_info: response?.data.data.shipping_info,
				})
			)

			dispatch(ordersRefrestUpdate())
			await completeProgress()
			setSelectPrice(0)
			close()
			clearFiles()
		} catch (error) {
			Toast({ type: 'error', message: 'No se pudo actualizar la orden' })
		} finally {
			close()
		}
	}

	const updateOrderStatus = async (status: OrderStatus) => {
		const response = await updateOrderStatusById(token ?? '', order?._id, {
			status,
		})

		if (response?.status === 409) {
			Toast({
				type: 'error',
				message: 'Hubo un error el actualizar el estado de la orden',
			})
			await failProgress()
			return
		}

		if (response?.status === 401) {
			dispatch(sessionRefrestUpdate())
			await failProgress()
			return
		}

		dispatch(updateStatusOrderFn({ _id: order?._id, status }))
		Toast({ type: 'success', message: 'Estado actualizado con exito' })
	}

	const updateOrderStatusConfirm = async (status: OrderStatus) => {
		try {
			ToastConfirm({
				message: `¿Quieres actualizar el estado a ${OrderStatusConvert(
					status
				)}?`,
				onConfirm: async () => {
					await updateOrderStatus(status)
				},
				onCancel: () => {
					Toast({
						type: 'info',
						message: 'Se cancelo la operacion',
					})
				},
				confirmLabel: 'Actualizar',
				cancelLabel: 'Cancelar',
			})
		} catch (error) {
			Toast({
				type: 'error',
				message: 'No se pudo actualizar el estado de la orden',
			})
		}
	}

	const rejectedOrder = async () => {
		try {
			if (!denialReason) {
				Toast({ type: 'error', message: 'Debes agregar un motivo de rechazo' })
				return
			}

			ToastConfirm({
				message: `¿Quieres rechazar el pedido?`,
				onConfirm: async () => {
					const response = await updateOrderDenialById(
						token ?? '',
						order?._id,
						{
							denial: denialReason,
							customerEmail: order.customer?.email ?? '',
							customerName: `${order.customer?.name ?? 'Usuario sin nombre'} ${
								order.customer?.lastname ?? 'Usuario sin apellido'
							}`,
						}
					)

					if (response?.status === 404) {
						Toast({
							type: 'error',
							message: 'Error pedido no encontrado',
						})
						await failProgress()
						return
					}

					if (response?.status === 409) {
						Toast({
							type: 'error',
							message: 'Hubo un error el actualizar el estado de la orden',
						})
						await failProgress()
						return
					}

					if (response?.status === 401) {
						dispatch(sessionRefrestUpdate())
						await failProgress()
						return
					}

					dispatch(
						updateDenialOrderFn({
							_id: order?._id,
							denial: {
								reason: denialReason,
							},
						})
					)
					dispatch(
						updateStatusOrderFn({
							_id: order?._id,
							status: OrderStatus.rejected,
						})
					)
					Toast({ type: 'success', message: 'Estado actualizado con exito' })
				},
				onCancel: () => {
					Toast({
						type: 'info',
						message: 'Se cancelo la operacion',
					})
				},
				confirmLabel: 'Rechazar',
				cancelLabel: 'Cancelar',
			})
		} catch (error) {
			Toast({ type: 'error', message: 'No se pudo rechazar la orden' })
		}
	}

	const refundOrder = () => {
		try {
			if (!refundedReason) {
				Toast({
					type: 'error',
					message: 'Debes agregar un motivo de devolucion',
				})
				return
			}

			ToastConfirm({
				message: `¿Quieres hacer una devolucion para este pedido?`,
				onConfirm: async () => {
					const response = await updateOrderRefundedById(
						token ?? '',
						order?._id,
						{ reason: refundedReason }
					)

					if (response?.status === 409) {
						Toast({
							type: 'error',
							message: 'Hubo un error el actualizar el estado de la orden',
						})
						await failProgress()
						return
					}

					if (response?.status === 401) {
						dispatch(sessionRefrestUpdate())
						await failProgress()
						return
					}

					dispatch(
						updateDenialOrderFn({
							_id: order?._id,
							denial: {
								reason: denialReason,
							},
						})
					)
					dispatch(
						updateStatusOrderFn({
							_id: order?._id,
							status: OrderStatus.rejected,
						})
					)
					Toast({ type: 'success', message: 'Estado actualizado con exito' })
				},
				onCancel: () => {
					Toast({
						type: 'info',
						message: 'Se cancelo la operacion',
					})
				},
				confirmLabel: 'Rechazar',
				cancelLabel: 'Cancelar',
			})
		} catch (error) {
			Toast({ type: 'error', message: 'No se pudo rechazar la orden' })
		}
	}

	console.log(order.products[0].type !== Entities.PLANS_PERSONALIZED)

	return (
		<Flex
			w='full'
			minW='370px'
			maxW='700px'
			flexDirection='column'
			alignItems='center'
		>
			{order?.status === OrderStatus.rejected && (
				<Box
					w='full'
					bg='red.50'
					border='0.5px solid'
					borderColor='red'
					p='2'
					borderRadius='md'
					mb='4'
				>
					<Heading
						fontWeight='bold'
						as='h3'
						color='gray.700'
						fontSize='md'
						mb='1'
					>
						Orden rechazada
					</Heading>
					<Text color='gray.600' fontSize='sm' fontWeight='400'>
						{order?.denial?.reason || 'Sin razon'}
					</Text>
				</Box>
			)}
			{order?.status === OrderStatus.refunded && (
				<Box
					w='full'
					bg='blue.50'
					border='0.5px solid'
					borderColor='blue'
					p='2'
					borderRadius='md'
					mb='4'
				>
					<Heading
						fontWeight='bold'
						as='h3'
						color='gray.700'
						fontSize='md'
						mb='1'
					>
						Orden reembolsada
					</Heading>
					<Text color='gray.600' fontSize='sm' fontWeight='400'>
						{order?.refunded?.reason || 'Sin razon'}
					</Text>
				</Box>
			)}
			<Box w='full'>
				<Heading
					as='h3'
					fontSize={{ base: 'lg', sm: 'xl' }}
					color='black'
					fontWeight='800'
					mb={{ base: 3, sm: 4 }}
				>
					Productos (
					{Array.isArray(order?.products) && order.products.length > 0
						? order.products.reduce(
								(total, product) =>
									total +
									(Array.isArray(product?.attributes)
										? product.attributes.reduce(
												(attrTotal, attr) => attrTotal + (attr?.quantity || 0),
												0
										  )
										: 0),
								0
						  )
						: 0}{' '}
					items)
				</Heading>

				<VStack align='stretch' gap={0} w='full'>
					{Array.isArray(order?.products) && order.products.length > 0 ? (
						order.products.map((product, productIndex) => (
							<Box key={product?._id ?? productIndex}>
								<Flex
									flexDirection='column'
									w='full'
									p={{ base: 3, sm: 4 }}
									bg='gray.50'
									borderRadius='md'
									mb={2}
								>
									<Flex align='center' justify='space-between'>
										<Flex gap={2} align='center'>
											<Text fontSize='sm' fontWeight='600' color='gray.700'>
												{product?.brand ?? 'Sin marca'}
											</Text>
											<Badge colorScheme='blue' size='sm'>
												{entityConvert(product?.type as Entities) ?? 'Sin tipo'}
											</Badge>
										</Flex>
										<Text fontSize='xs' color='gray.500'>
											Envío:{' '}
											{formatCurrency(product?.shipping_info?.price ?? 0)}
										</Text>
									</Flex>
									<Flex flexDirection='column'>
										{product.discount > 0 && (
											<Text fontSize='xs' color='gray.500'>
												<b>Descuento del producto:</b> {product.discount}%
											</Text>
										)}
										{order?.coupon &&
											((order.coupon.type === TypesCoupon.AMOUNT &&
												(order.coupon.amount ?? 0) > 0) ||
												(order.coupon.type === TypesCoupon.DISCOUNT &&
													(order.coupon.discount ?? 0) > 0)) && (
												<Text fontSize='xs' color='gray.500'>
													<b>Descuento del cupón: </b>
													{order.coupon.type === TypesCoupon.AMOUNT
														? formatCurrency(order.coupon.amount ?? 0)
														: `${order.coupon.discount ?? 0} %`}
												</Text>
											)}
									</Flex>
								</Flex>

								<Flex direction='column' gap={2}>
									{Array.isArray(product?.attributes) &&
										product.attributes.map((attribute) => {
											const renderAttributeDetails = () => {
												const names = Array.isArray(attribute.name)
													? attribute.name
													: [attribute.name].filter(Boolean)
												const values = Array.isArray(attribute.value)
													? attribute.value
													: [attribute.value].filter(Boolean)
												const references = Array.isArray(attribute.reference)
													? attribute.reference
													: [attribute.reference].filter(Boolean)

												const maxLength = Math.max(
													names.length,
													values.length,
													references.length
												)
												const attributeItems = []

												for (let i = 0; i < maxLength; i++) {
													attributeItems.push({
														name: names[i] || 'Sin tipo',
														value: values[i] || 'Sin valor',
														reference: references[i] || 'Sin referencia',
													})
												}

												return attributeItems
											}

											const attributeItems = renderAttributeDetails()
											const hasMultipleAttributes = attributeItems.length > 1

											const getDisplayTitle = () => {
												if (attributeItems.length === 0)
													return 'Atributo sin datos'

												const uniqueTypes = [
													...new Set(
														attributeItems.map((item) =>
															nameAttributeConvert(item.name)
														)
													),
												]

												if (uniqueTypes.length === 1) {
													const values = attributeItems
														.map((item) => item.value)
														.join(', ')
													return `${uniqueTypes[0]}: ${values}`
												} else {
													return uniqueTypes.join(' + ')
												}
											}

											const getMainReference = () => {
												const validReferences = attributeItems.filter(
													(item) =>
														item.reference &&
														item.reference &&
														item.reference.startsWith('#')
												)
												return validReferences.length > 0
													? validReferences[0].reference
													: null
											}

											return (
												<Flex
													key={attribute?._id ?? Math.random()}
													w='full'
													p={{ base: 3, sm: 4 }}
													borderRadius='md'
													border='1px'
													borderColor='gray.200'
													_hover={{
														bg: 'gray.50',
														borderColor: 'gray.300',
														transform: 'translateY(-1px)',
														shadow: 'sm',
													}}
													transition='all 0.2s'
													align='center'
													justify='space-between'
												>
													<Flex align='center' flex={2}>
														<Box
															p={2}
															bg='white'
															borderRadius='md'
															border='1px'
															borderColor='gray.200'
															mr={{ base: 2, sm: 3 }}
														>
															<Image
																src={`${API_URL}/${attribute?.image?.url}`}
																alt={`${product?.name ?? ''} - ${
																	attribute?._id ?? ''
																}`}
																w={{ base: '50px', sm: '60px' }}
																h={{ base: '50px', sm: '60px' }}
																objectFit='cover'
																borderRadius='sm'
															/>
														</Box>

														<Box flex={1}>
															<Text
																fontSize={{ base: 'sm', sm: 'md' }}
																fontWeight='700'
																color='gray.800'
															>
																{product?.name ?? 'Producto sin nombre'}
															</Text>

															<Flex direction='column' gap={1} mt={1}>
																<Flex align='center' gap={2}>
																	<Text
																		fontSize='xs'
																		color='gray.600'
																		fontWeight='600'
																		textTransform='capitalize'
																	>
																		{getDisplayTitle()}
																	</Text>
																	{getMainReference() && (
																		<Box
																			w={3}
																			h={3}
																			borderRadius='full'
																			bg={getMainReference() || ''}
																			border='1px'
																			borderColor='gray.300'
																		/>
																	)}
																</Flex>

																{hasMultipleAttributes && (
																	<Flex flexWrap='wrap' gap={1} mt={1}>
																		{attributeItems
																			.slice(0, 3)
																			.map((item, index) => (
																				<Box
																					key={index}
																					bg='gray.100'
																					px={2}
																					py={1}
																					borderRadius='sm'
																					fontSize='10px'
																					color='gray.600'
																				>
																					{nameAttributeConvert(item.name)}:{' '}
																					{item.value}
																				</Box>
																			))}
																		{attributeItems.length > 3 && (
																			<Box
																				bg='blue.100'
																				px={2}
																				py={1}
																				borderRadius='sm'
																				fontSize='10px'
																				color='blue.600'
																				fontWeight='600'
																			>
																				+{attributeItems.length - 3} más
																			</Box>
																		)}
																	</Flex>
																)}
															</Flex>

															<Text fontSize='xs' color='gray.400' mt={0.5}>
																ID: {(attribute?._id ?? '').slice(0, 8)}...
															</Text>
														</Box>
													</Flex>

													<Flex flex={0.5} justify='center' align='center'>
														<Badge
															px={3}
															py={1}
															borderRadius='full'
															colorScheme='blue'
															variant='subtle'
														>
															Qty: {attribute?.quantity || 0}
														</Badge>
													</Flex>

													<Flex flex={1} justify='flex-end' align='center'>
														<Flex direction='column' gap={0} textAlign='right'>
															{(attribute?.productDiscount || 0) > 0 && (
																<Text
																	fontSize='xs'
																	color='gray.400'
																	textDecoration='line-through'
																>
																	{formatCurrency(attribute?.subtotal || 0)}
																</Text>
															)}
															<Text
																fontSize={{ base: 'sm', sm: 'md' }}
																color='green.600'
																fontWeight='bold'
															>
																{formatCurrency(attribute?.total || 0)}
															</Text>
															{(attribute?.price || 0) > 0 && (
																<Text fontSize='xs' color='gray.500'>
																	{formatCurrency(attribute?.price || 0)} c/u
																</Text>
															)}
														</Flex>
													</Flex>
												</Flex>
											)
										})}
								</Flex>

								{productIndex < order.products.length - 1 && (
									<Box h='1px' bg='gray.200' my={4} w='full' />
								)}
							</Box>
						))
					) : (
						<Box
							w='full'
							p={{ base: 8, sm: 12 }}
							textAlign='center'
							border='2px dashed'
							borderColor='gray.200'
							borderRadius='lg'
						>
							<Text fontSize='4xl' mb={2}>
								📦
							</Text>
							<Heading
								fontSize={{ base: 'lg', sm: 'xl' }}
								color='gray.600'
								fontWeight='600'
							>
								No hay productos en esta orden
							</Heading>
							<Text fontSize='sm' color='gray.500' mt={2}>
								Los productos aparecerán aquí cuando se agreguen a la orden
							</Text>
						</Box>
					)}
				</VStack>

				{Array.isArray(order?.products) && order.products.length > 0 && (
					<Box
						mt={{ base: 4, sm: 6 }}
						p={{ base: 4, sm: 5 }}
						bg='gray.50'
						borderRadius='lg'
						border='1px'
						borderColor='gray.200'
					>
						<Stack gap={3} align='stretch'>
							<Flex justify='space-between' align='center'>
								<Text fontSize='sm' color='gray.600'>
									Subtotal (
									{order.products.reduce(
										(total, product) =>
											total +
											(Array.isArray(product?.attributes)
												? product.attributes.reduce(
														(attrTotal, attr) =>
															attrTotal + (attr?.quantity || 0),
														0
												  )
												: 0),
										0
									)}{' '}
									items)
								</Text>
								<Text fontSize='sm' fontWeight='600' color='gray.800'>
									{formatCurrency(
										order.products.reduce(
											(total, product) => total + (product?.subtotal || 0),
											0
										)
									)}
								</Text>
							</Flex>

							{order.products.reduce(
								(total, product) =>
									total +
									(product?.productDiscount || 0) +
									(product?.couponDiscount || 0),
								0
							) > 0 && (
								<Flex justify='space-between' align='center'>
									<Text fontSize='sm' color='red.600'>
										Descuentos aplicados
									</Text>
									<Text fontSize='sm' fontWeight='600' color='red.600'>
										-
										{formatCurrency(
											order.products.reduce(
												(total, product) =>
													total +
													(product?.productDiscount || 0) +
													(product?.couponDiscount || 0),
												0
											)
										)}
									</Text>
								</Flex>
							)}

							<Flex justify='space-between' align='center'>
								<Text fontSize='sm' color='gray.600'>
									Costos de envío
								</Text>
								<Text fontSize='sm' fontWeight='600' color='gray.800'>
									{formatCurrency(
										order.products.reduce(
											(total, product) => total + (product?.totalShipping || 0),
											0
										)
									)}
								</Text>
							</Flex>

							<Separator borderColor='gray.300' />

							<Flex justify='space-between' align='center'>
								<Text fontSize='lg' fontWeight='700' color='gray.800'>
									Total
								</Text>
								<Text fontSize='xl' fontWeight='800' color='green.600'>
									{formatCurrency(
										(Array.isArray(order?.products) && order.products.length > 0
											? order.products.reduce((total, product) => {
													const subtotal = product?.subtotal || 0
													const discounts = product?.totalDiscount || 0
													const shipping = product?.totalShipping || 0
													return total + subtotal - discounts + shipping
											  }, 0)
											: 0
										).toFixed(2)
									)}
								</Text>
							</Flex>
						</Stack>
					</Box>
				)}
			</Box>

			<Box
				w='full'
				borderTop='2px solid'
				borderColor='gray'
				pt={{ base: '2', sm: '4' }}
				mt={{ base: '2', sm: '4' }}
			>
				<Heading
					as='h3'
					fontSize={{ base: 'lg', sm: 'xl' }}
					color='black'
					fontWeight='700'
					mb={{ base: '2', sm: '4' }}
				>
					informacion de la orden
				</Heading>
				<Flex
					w='full'
					alignItems='center'
					mt={{ base: '1', sm: '2' }}
					mb={{ base: '1', sm: '2' }}
					justifyContent='space-between'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'md', sm: 'lg' }}
						color='gray'
						fontWeight='700'
					>
						Fecha
					</Heading>
					<Text
						color='black'
						fontSize={{ base: 'sm', sm: 'md' }}
						fontWeight='700'
					>
						{DateConvert(order?._created ?? 'Sin fecha')}
					</Text>
				</Flex>
				<Flex
					w='full'
					alignItems='center'
					mt={{ base: '1', sm: '2' }}
					mb={{ base: '1', sm: '2' }}
					justifyContent='space-between'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'md', sm: 'lg' }}
						color='gray'
						fontWeight='700'
					>
						Codigo de orden
					</Heading>
					<Text
						color='black'
						fontSize={{ base: 'sm', sm: 'md' }}
						fontWeight='700'
					>
						{order?.code ?? 'Sin codigo'}
					</Text>
				</Flex>
				<Flex
					w='full'
					alignItems='center'
					mt={{ base: '1', sm: '2' }}
					mb={{ base: '1', sm: '2' }}
					justifyContent='space-between'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'md', sm: 'lg' }}
						color='gray'
						fontWeight='700'
					>
						Metodo de pago
					</Heading>
					<Text
						color='black'
						fontSize={{ base: 'sm', sm: 'md' }}
						fontWeight='700'
					>
						{PaymethodsConvert(order?.payment_info?.paymethod)}
					</Text>
				</Flex>
				<Flex
					w='full'
					alignItems='center'
					mt={{ base: '1', sm: '2' }}
					mb={{ base: '1', sm: '2' }}
					justifyContent='space-between'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'md', sm: 'lg' }}
						color='gray'
						fontWeight='700'
					>
						Validacion de transaccion
					</Heading>
					<Text
						color='black'
						fontSize={{ base: 'sm', sm: 'md' }}
						fontWeight='700'
					>
						{WompiStatusConvert(order?.payment_info?.status)}
					</Text>
				</Flex>
				<Flex
					w='full'
					alignItems='center'
					mt={{ base: '1', sm: '2' }}
					mb={{ base: '1', sm: '2' }}
					justifyContent='space-between'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'md', sm: 'lg' }}
						color='gray'
						fontWeight='700'
					>
						Estado
					</Heading>
					<Text
						color={OrderStatusColor(order?.status)}
						fontSize={{ base: 'sm', sm: 'md' }}
						fontWeight='700'
					>
						{OrderStatusConvert(order?.status)}
					</Text>
				</Flex>
			</Box>

			<Box
				w='full'
				borderTop='2px solid'
				borderColor='gray'
				pt={{ base: '2', sm: '4' }}
				mt={{ base: '2', sm: '4' }}
			>
				<Heading
					as='h3'
					fontSize={{ base: 'lg', sm: 'xl' }}
					color='black'
					fontWeight='700'
					mb={{ base: '2', sm: '4' }}
				>
					informacion del usuario
				</Heading>

				<Flex w='full' alignItems='center' justifyContent='space-between'>
					<Flex>
						<Box
							p={{ base: '1', sm: '2' }}
							boxShadow='sm'
							borderRadius='full'
							w='60px'
							h='60px'
						>
							<Image
								src={`${API_URL}/${order.customer?.images?.[0].url}`}
								alt={`${order.customer?.name ?? 'Usuario'} - perfil`}
								w='full'
								h='full'
								borderRadius='full'
								objectFit='cover'
							/>
						</Box>
						<Box
							flex={2}
							ml={{ base: '1', sm: '2' }}
							pt={{ base: '1', sm: '2' }}
							pb={{ base: '1', sm: '2' }}
						>
							<Text
								fontSize={{ base: 'sm', sm: 'lg' }}
								color='gray.700'
								fontWeight='800'
							>
								{order.customer?.name ?? 'Usuario sin nombre'}
							</Text>
							<Text fontSize={{ base: 'xs', sm: 'sm' }} color='gray.500'>
								{order.customer?.email ?? 'Usuario sin email'}
							</Text>
						</Box>
					</Flex>
					<Text fontSize={{ base: 'xs', sm: 'sm' }} color='gray.500'>
						{getJoinUser(order.customer?._created ?? 'Sin fecha')}
					</Text>
				</Flex>
			</Box>

			<Box
				w='full'
				borderTop='2px solid'
				borderColor='gray'
				pt={{ base: '2', sm: '4' }}
				mt={{ base: '2', sm: '4' }}
			>
				<Heading
					as='h3'
					fontSize={{ base: 'lg', sm: 'xl' }}
					color='black'
					fontWeight='700'
					mb={{ base: '2', sm: '4' }}
				>
					Información de Envío
				</Heading>
				<Flex
					w='full'
					alignItems='center'
					mt={{ base: '1', sm: '2' }}
					mb={{ base: '1', sm: '2' }}
					justifyContent='space-between'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'md', sm: 'lg' }}
						color='gray'
						fontWeight='700'
					>
						Nombre completo
					</Heading>
					<Text
						color='black'
						fontSize={{ base: 'sm', sm: 'md' }}
						fontWeight='700'
					>
						{order.customer?.address?.[0]?.fullname ?? 'Sin nombre'}
					</Text>
				</Flex>
				<Flex
					w='full'
					alignItems='center'
					mt={{ base: '1', sm: '2' }}
					mb={{ base: '1', sm: '2' }}
					justifyContent='space-between'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'md', sm: 'lg' }}
						color='gray'
						fontWeight='700'
					>
						{order.customer?.address?.[0]?.document_type ??
							'Sin tipo de documento'}
					</Heading>
					<Text
						color='black'
						fontSize={{ base: 'sm', sm: 'md' }}
						fontWeight='700'
					>
						Cédula de ciudadanía
					</Text>
				</Flex>
				<Flex
					w='full'
					alignItems='center'
					mt={{ base: '1', sm: '2' }}
					mb={{ base: '1', sm: '2' }}
					justifyContent='space-between'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'md', sm: 'lg' }}
						color='gray'
						fontWeight='700'
					>
						Documento
					</Heading>
					<Text
						color='black'
						fontSize={{ base: 'sm', sm: 'md' }}
						fontWeight='700'
					>
						{order.customer?.address?.[0]?.document ?? ''}
					</Text>
				</Flex>
				<Flex
					w='full'
					alignItems='center'
					mt={{ base: '1', sm: '2' }}
					mb={{ base: '1', sm: '2' }}
					justifyContent='space-between'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'md', sm: 'lg' }}
						color='gray'
						fontWeight='700'
					>
						Departamento
					</Heading>
					<Text
						color='black'
						fontSize={{ base: 'sm', sm: 'md' }}
						fontWeight='700'
					>
						{order.customer?.address?.[0]?.province ?? 'Sin departamento'}
					</Text>
				</Flex>
				<Flex
					w='full'
					alignItems='center'
					mt={{ base: '1', sm: '2' }}
					mb={{ base: '1', sm: '2' }}
					justifyContent='space-between'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'md', sm: 'lg' }}
						color='gray'
						fontWeight='700'
					>
						Ciudad
					</Heading>
					<Text
						color='black'
						fontSize={{ base: 'sm', sm: 'md' }}
						fontWeight='700'
					>
						{order.customer?.address?.[0]?.city ?? ''}
					</Text>
				</Flex>
				<Flex
					w='full'
					alignItems='center'
					mt={{ base: '1', sm: '2' }}
					mb={{ base: '1', sm: '2' }}
					justifyContent='space-between'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'md', sm: 'lg' }}
						color='gray'
						fontWeight='700'
					>
						Tipo de calle
					</Heading>
					<Text
						color='black'
						fontSize={{ base: 'sm', sm: 'md' }}
						fontWeight='700'
					>
						{order.customer?.address?.[0]?.street_type ?? ''}
					</Text>
				</Flex>
				<Flex
					w='full'
					alignItems='center'
					mt={{ base: '1', sm: '2' }}
					mb={{ base: '1', sm: '2' }}
					justifyContent='space-between'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'md', sm: 'lg' }}
						color='gray'
						fontWeight='700'
					>
						Calle
					</Heading>
					<Text
						color='black'
						fontSize={{ base: 'sm', sm: 'md' }}
						fontWeight='700'
					>
						{order.customer?.address?.[0]?.street ?? ''}
					</Text>
				</Flex>
				<Flex
					w='full'
					alignItems='center'
					mt={{ base: '1', sm: '2' }}
					mb={{ base: '1', sm: '2' }}
					justifyContent='space-between'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'md', sm: 'lg' }}
						color='gray'
						fontWeight='700'
					>
						Número de casa
					</Heading>
					<Text
						color='black'
						fontSize={{ base: 'sm', sm: 'md' }}
						fontWeight='700'
					>
						{order.customer?.address?.[0]?.number_home ?? ''}
					</Text>
				</Flex>
				<Flex
					w='full'
					alignItems='center'
					mt={{ base: '1', sm: '2' }}
					mb={{ base: '1', sm: '2' }}
					justifyContent='space-between'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'md', sm: 'lg' }}
						color='gray'
						fontWeight='700'
					>
						Tipo de casa
					</Heading>
					<Text
						color='black'
						fontSize={{ base: 'sm', sm: 'md' }}
						fontWeight='700'
					>
						{order.customer?.address?.[0]?.type_home ?? ''}
					</Text>
				</Flex>
				<Flex
					w='full'
					alignItems='center'
					mt={{ base: '1', sm: '2' }}
					mb={{ base: '1', sm: '2' }}
					justifyContent='space-between'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'md', sm: 'lg' }}
						color='gray'
						fontWeight='700'
					>
						Teléfono celular
					</Heading>
					<Text
						color='black'
						fontSize={{ base: 'sm', sm: 'md' }}
						fontWeight='700'
					>
						{order.customer?.address?.[0]?.phone ?? ''}
					</Text>
				</Flex>
				<Flex
					w='full'
					alignItems='center'
					mt={{ base: '1', sm: '2' }}
					mb={{ base: '1', sm: '2' }}
					justifyContent='space-between'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'md', sm: 'lg' }}
						color='gray'
						fontWeight='700'
					>
						Referencias adicionales
					</Heading>
					<Text
						color='black'
						fontSize={{ base: 'sm', sm: 'md' }}
						fontWeight='700'
					>
						{order.customer?.address?.[0]?.additional_references ??
							'Sin referencias'}
					</Text>
				</Flex>
			</Box>

			{order.products[0].type !== Entities.PLANS_PERSONALIZED &&
				[
					OrderStatus.processing,
					OrderStatus.delivered,
					OrderStatus.on_the_way,
					OrderStatus.refunded,
				].includes(order?.status) && (
					<form
						className='w-full'
						onSubmit={handleSubmit(onSubmit, async (err) => {
							console.log(err)
						})}
					>
						<Box
							w='full'
							borderTop='2px solid'
							borderColor='gray'
							pt={{ base: '2', sm: '4' }}
							mt={{ base: '2', sm: '4' }}
						>
							<Heading
								as='h3'
								fontSize={{ base: 'lg', sm: 'xl' }}
								color='black'
								fontWeight='700'
								mb={{ base: '2', sm: '4' }}
							>
								Comprobantes de envio
							</Heading>

							<InputGroupContent
								label='Nombre de la empresa de envios'
								placeholder='Ingresa el nombre de la empresa de envios'
								type='text'
								name='company'
								defaultValue={order.shipping_info?.company || ''}
								register={register}
								errors={errors}
								disabled={[
									OrderStatus.delivered,
									OrderStatus.refunded,
								].includes(order?.status)}
								readOnlyActive={[
									OrderStatus.delivered,
									OrderStatus.refunded,
								].includes(order?.status)}
								required={true}
							/>
							<InputGroupContent
								label='Dirección de la empresa de envios'
								placeholder='Dirección el Direccion de la empresa de envios'
								type='text'
								defaultValue={order.shipping_info?.company_address || ''}
								name='company_address'
								register={register}
								errors={errors}
								disabled={[
									OrderStatus.delivered,
									OrderStatus.refunded,
								].includes(order?.status)}
								readOnlyActive={[
									OrderStatus.delivered,
									OrderStatus.refunded,
								].includes(order?.status)}
								required={true}
							/>
							<InputGroupContent
								label='Codigo de envio'
								placeholder='Ingresa el codigo del pedido del ticket'
								type='text'
								defaultValue={order.shipping_info?.code || ''}
								name='code'
								register={register}
								disabled={[
									OrderStatus.delivered,
									OrderStatus.refunded,
								].includes(order?.status)}
								readOnlyActive={[
									OrderStatus.delivered,
									OrderStatus.refunded,
								].includes(order?.status)}
								errors={errors}
								required={true}
							/>

							<InputGroupContent
								label='Tiempo estimado en dias'
								placeholder='Ingresa el tiempo estimado en dias para la entrega'
								type='number'
								name='estimated_date'
								register={register}
								defaultValue={order.shipping_info?.estimated_date || 0}
								disabled={[
									OrderStatus.delivered,
									OrderStatus.refunded,
								].includes(order?.status)}
								readOnlyActive={[
									OrderStatus.delivered,
									OrderStatus.refunded,
								].includes(order?.status)}
								errors={errors}
								required={true}
							/>

							<Select
								name='type'
								label='Typo de envio'
								placeholder='Selecciona el tipo de envio'
								options={shippingTypeMap}
								defaultValue={{
									label:
										shippingTypeMap.find(
											(obj) => obj.value === order.shipping_info.type
										)?.label ?? '',
									value: order.shipping_info?.type || '',
								}}
								control={control}
								isDisabled={[
									OrderStatus.delivered,
									OrderStatus.refunded,
								].includes(order?.status)}
								isRequired={true}
							/>

							<InputGroupContent
								label='Precio del envio (Opcional)'
								isPrice={true}
								defaultValue={order.shipping_info?.price || 0}
								change={(e: React.ChangeEvent<HTMLInputElement>) => {
									setSelectPrice(Number(e.target.value) || 0)
								}}
								disabled={[
									OrderStatus.delivered,
									OrderStatus.refunded,
								].includes(order?.status)}
								readOnlyActive={[
									OrderStatus.delivered,
									OrderStatus.refunded,
								].includes(order?.status)}
							/>

							<Box
								w='full'
								display='flex'
								justifyContent='start'
								alignItems='center'
								mb='4'
							>
								<Heading
									as='h4'
									fontSize={{ base: 'lg', md: 'xl' }}
									color='black'
									fontWeight='900'
								>
									Comprobante o guia de envio (max 1 foto)
								</Heading>
							</Box>
							<Box w='full' mb='4'>
								<Grid
									w='full'
									border='0.1px solid gray'
									borderRadius='lg'
									p='4'
									gap='4'
									templateColumns={{
										base: 'repeat(2, 1fr)',
										sm: 'repeat(3, 1fr)',
									}}
								>
									{order.shipping_info?.evidenced &&
										order.shipping_info.evidenced._id &&
										order.shipping_info.evidenced.url &&
										order.shipping_info.evidenced.url !== 'files/image/0' && (
											<ImageItem
												id={order.shipping_info.evidenced._id}
												name={order.shipping_info.evidenced.url}
												preview={`${API_URL}/${order.shipping_info.evidenced.url}`}
												removeFile={() => {
													if (
														[
															OrderStatus.processing,
															OrderStatus.on_the_way,
														].includes(order?.status)
													) {
														deleteCurrentImage()
													}
												}}
											/>
										)}

									{files &&
										isUploadImage &&
										files.map((item, index) => (
											<ImageItem
												key={index}
												id={item.id}
												name={item.name}
												preview={item.preview ?? ''}
												removeFile={removeFile}
											/>
										))}

									<Box position='relative' h='12vh'>
										{isProcessing ? (
											<SpinnerLoading />
										) : (
											<>
												<Input
													type='file'
													accept='image/*'
													multiple
													ref={inputRef}
													onChange={(e) => {
														handleFileChange(e)
														deleteCurrentImage()
													}}
													opacity='0'
													position='absolute'
													h='full'
													w='full'
													disabled={
														files.length >= 1 ||
														![
															OrderStatus.processing,
															OrderStatus.on_the_way,
														].includes(order?.status)
													}
													cursor='pointer'
													zIndex='2'
												/>
												<Box
													h='full'
													w='full'
													border='2px dashed gray'
													display='flex'
													alignItems='center'
													justifyContent='center'
													borderRadius='md'
													position='relative'
													zIndex='1'
													p='2'
													bg='gray.100'
													_hover={{ bg: 'gray.200' }}
												>
													<Icon
														icon={files.length >= 1 ? 'disabled' : 'camera'}
														size='xl'
														color='gray'
													/>
													<Text
														ml='2'
														color='gray.500'
														fontSize='sm'
														display={{ base: 'none', md: 'block' }}
													>
														{files.length >= 1
															? 'Candidad Max'
															: 'Añadir Imagen'}
													</Text>
												</Box>
											</>
										)}
									</Box>
								</Grid>
							</Box>

							{[OrderStatus.processing, OrderStatus.on_the_way].includes(
								order?.status
							) && (
								<Box pt={{ base: '2', sm: '4' }}>
									<Submit
										text={
											order?.status === OrderStatus.processing
												? 'Agregar Informacion de envio'
												: 'Actualizar Informacion de envio'
										}
									/>
								</Box>
							)}
						</Box>
					</form>
				)}

			<Box
				w='full'
				borderTop='2px solid'
				borderColor='gray'
				pt={{ base: '2', sm: '4' }}
				mt={{ base: '2', sm: '4' }}
			>
				<Heading
					as='h3'
					fontSize={{ base: 'lg', sm: 'xl' }}
					color='black'
					fontWeight='700'
					mb={{ base: '2', sm: '4' }}
				>
					Acciones
				</Heading>

				<Box w='full'>
					{[
						OrderStatus.new,
						OrderStatus.pending,
						OrderStatus.processing,
					].includes(order?.status) && (
						<AccordionRoot>
							<AccordionItem value='reject'>
								<AccordionItemTrigger
									p={{ base: '3', sm: '4' }}
									border='1px solid'
									borderColor='red.400'
									borderRadius='lg'
									cursor='pointer'
								>
									<Box flex='1' textAlign='left'>
										<Text
											fontWeight='bold'
											color='red.600'
											fontSize={{ base: 'lg', sm: 'xl' }}
										>
											Rechazar
										</Text>
									</Box>
								</AccordionItemTrigger>
								<AccordionItemContent>
									<Box
										w='full'
										mt='2'
										p={{ base: '3', sm: '4' }}
										borderRadius='lg'
									>
										<InputGroup w='full' mt='4' mb='4'>
											<Field
												label='Motivo de rechazo'
												required
												labelProps={{ fontSize: 'md' }}
											>
												<Textarea
													placeholder='Ingresa el motivo del rechazo'
													rows={5}
													p='5'
													fontSize='lg'
													outline='none'
													resize='none'
													defaultValue={denialReason}
													onChange={(e) => setDenialReason(e.target.value)}
												/>
											</Field>
										</InputGroup>
										<Box pt={{ base: '2', sm: '4' }}>
											<Submit
												type='button'
												click={() => rejectedOrder()}
												text='Rechazar'
												bg='red.600'
											/>
										</Box>
									</Box>
								</AccordionItemContent>
							</AccordionItem>
						</AccordionRoot>
					)}
					{order?.status === OrderStatus.delivered && (
						<AccordionRoot>
							<AccordionItem value='reject'>
								<AccordionItemTrigger
									p={{ base: '3', sm: '4' }}
									border='1px solid'
									borderColor='red.400'
									borderRadius='lg'
									cursor='pointer'
								>
									<Box flex='1' textAlign='left'>
										<Text
											fontWeight='bold'
											color='red.600'
											fontSize={{ base: 'lg', sm: 'xl' }}
										>
											Hacer una devolucion
										</Text>
									</Box>
								</AccordionItemTrigger>
								<AccordionItemContent>
									<Box
										w='full'
										mt='2'
										p={{ base: '3', sm: '4' }}
										borderRadius='lg'
									>
										<InputGroup w='full' mt='4' mb='4'>
											<Field
												label='Motivo de la devolucion'
												required
												labelProps={{ fontSize: 'md' }}
											>
												<Textarea
													placeholder='Ingresa el motivo de la devolucion'
													rows={5}
													p='5'
													fontSize='lg'
													outline='none'
													resize='none'
													defaultValue={refundedReason}
													onChange={(e) => setRefundedReason(e.target.value)}
												/>
											</Field>
										</InputGroup>
										<Box pt={{ base: '2', sm: '4' }}>
											<Submit
												type='button'
												click={() => refundOrder()}
												text='Hacer devolucion'
												bg='red.600'
											/>
										</Box>
									</Box>
								</AccordionItemContent>
							</AccordionItem>
						</AccordionRoot>
					)}
					<Button
						w='full'
						mt={{ base: '2', sm: '4' }}
						p={{ base: '3', sm: '4' }}
						bg='black'
						color='white'
						fontSize={{ base: 'md', sm: 'lg' }}
						fontWeight='800'
						borderRadius='lg'
						_hover={{ bg: 'gray.800' }}
						onClick={() => {
							const status = order?.status
							const productType = order?.products?.[0]?.type

							if (status === OrderStatus.new) {
								updateOrderStatusConfirm(OrderStatus.pending)
								return
							}

							if (
								status === OrderStatus.processing &&
								productType === Entities.PLANS_PERSONALIZED
							) {
								updateOrderStatusConfirm(OrderStatus.on_the_way)
								return
							}

							if (status === OrderStatus.pending) {
								updateOrderStatusConfirm(OrderStatus.processing)
								return
							}

							if (status === OrderStatus.on_the_way) {
								updateOrderStatusConfirm(OrderStatus.delivered)
							}
						}}
						disabled={
							order.products[0].type !== Entities.PLANS_PERSONALIZED
								? [
										OrderStatus.delivered,
										OrderStatus.rejected,
										OrderStatus.processing,
										OrderStatus.refunded,
								  ].includes(order?.status)
								: [
										OrderStatus.delivered,
										OrderStatus.rejected,
										OrderStatus.refunded,
								  ].includes(order?.status)
						}
					>
						{order?.status === OrderStatus.new && 'Actualizar a pendiente'}
						{order?.status === OrderStatus.pending && 'Actualizar a en proceso'}
						{order.products[0].type !== Entities.PLANS_PERSONALIZED &&
							order?.status === OrderStatus.processing &&
							'Agrega la informacion de envio'}
						{order?.status === OrderStatus.on_the_way &&
							'Actualizar a entregado'}
						{order.products[0].type === Entities.PLANS_PERSONALIZED &&
							order?.status === OrderStatus.processing &&
							' Actualizar a en camino'}
						{order?.status === OrderStatus.delivered && 'Orden ya entregada'}
						{order?.status === OrderStatus.rejected && 'Orden fue rechazada'}
						{order?.status === OrderStatus.refunded && 'Orden fue reembolsada'}
					</Button>
				</Box>
			</Box>
		</Flex>
	)
}

export default OpenOrder
